import {
    RemovableRow,
    InputControl,
    Picker,
    NumberInput,
    ColorPickerInput,
    Form,
    Accordion,
    InputLabel,
    IconButton,
    Spacer,
    StyleData,
    Text,
    TextInput,
    useTheme,
} from "@venuepos/react-common";
import { IProductInput, SupportedCurrenciesType } from "lib";
import { useTranslation } from "locales";
import React, { useMemo } from "react";
import { View } from "react-native";
import { Divider } from "react-native-paper";

export const DiscountTemplateRow = React.memo(function DiscountTemplateRow({
    item,
    index,
    isExternal,
    currency,
    styles,
    theme,
    t,
    onDiscountTemplateChange,
    onRemoveDiscountTemplate,
}: {
    item: NonNullable<IProductInput["discountTemplate"]>[number];
    index: number;
    isExternal: boolean;
    currency: SupportedCurrenciesType;
    styles: StyleData;
    theme: ReturnType<typeof useTheme>;
    t: ReturnType<typeof useTranslation>[0];
    onDiscountTemplateChange: (
        value: any,
        type: "name" | "type" | "value" | "color",
        index: number
    ) => void;
    onRemoveDiscountTemplate: (_item: any, index: number) => void;
}) {
    return (
        <RemovableRow
            key={`discount_template_row:${item.name}_${index}`}
            onRemove={() => {
                onRemoveDiscountTemplate(item, index);
            }}
            disabled={isExternal}
            closePosition="top"
            style={styles.removableRow}
        >
            <View style={styles.discountRow}>
                <TextInput
                    style={[styles.textInput]}
                    placeholder={t(
                        "backoffice.product_form.enter_discount_template_name",
                        "Enter discount template name"
                    )}
                    value={item.name}
                    onBlur={event =>
                        onDiscountTemplateChange(
                            event.nativeEvent.text,
                            "name",
                            index
                        )
                    }
                    disabled={isExternal}
                    required={true}
                    label={t(
                        "backoffice.product_form.discount_template_name",
                        "Name"
                    )}
                    testID="discountTemplate"
                />

                <InputControl
                    description={
                        <>
                            {" "}
                            <Text style={theme.styles.inputDescription}>
                                <Text
                                    style={[
                                        theme.styles.inputDescription,
                                        styles.boldUppercase,
                                    ]}
                                >
                                    {`${t(
                                        "backoffice.discount_form.value_type.percentage",
                                        "Percentage"
                                    )}: `}
                                </Text>
                                {t(
                                    "backoffice.discount_form.value_type_description.percentage",
                                    "If set to 25%, the new order price would become 300, since 25% of 400 is 100, so 400 - 100 = 300."
                                )}
                            </Text>
                            <Text style={theme.styles.inputDescription}>
                                <Text
                                    style={[
                                        theme.styles.inputDescription,
                                        styles.boldUppercase,
                                    ]}
                                >
                                    {`${t(
                                        "backoffice.discount_form.value_type.relative",
                                        "Relative"
                                    )}: `}
                                </Text>
                                {t(
                                    "backoffice.discount_form.value_type_description.relative",
                                    "If set to 100, the new order price would become 300, since 400 - 100 = 300."
                                )}
                            </Text>
                            <Text style={theme.styles.inputDescription}>
                                <Text
                                    style={[
                                        theme.styles.inputDescription,
                                        styles.boldUppercase,
                                    ]}
                                >
                                    {`${t(
                                        "backoffice.discount_form.value_type.absolute",
                                        "Absolute"
                                    )}: `}
                                </Text>
                                {t(
                                    "backoffice.discount_form.value_type_description.absolute",
                                    "If set to 100, the new order price would become 100"
                                )}
                            </Text>
                        </>
                    }
                >
                    <Picker
                        onValueChange={value => {
                            onDiscountTemplateChange(value, "type", index);
                        }}
                        selectedValue={item.type}
                        disabled={isExternal}
                        required={true}
                        label={t(
                            "backoffice.product_form.discount_template_type",
                            "Type"
                        )}
                    >
                        <Picker.Item
                            label={t(
                                "backoffice.product_form.choose_discount_template_type",
                                "Choose a type"
                            )}
                            value=""
                        />
                        <Picker.Item
                            label={t(
                                "backoffice.product_form.choose_discount_template_type_percentage",
                                "Percentage"
                            )}
                            value="PERCENTAGE"
                        />
                        <Picker.Item
                            label={t(
                                "backoffice.product_form.choose_discount_template_type_absolute",
                                "Absolute"
                            )}
                            value="ABSOLUTE"
                        />
                        <Picker.Item
                            label={t(
                                "backoffice.product_form.choose_discount_template_type_relative",
                                "Relative"
                            )}
                            value="RELATIVE"
                        />
                    </Picker>
                </InputControl>

                {item.type === "PERCENTAGE" ? (
                    <InputControl key="percentage">
                        <NumberInput
                            label={t("backoffice.discount_form.value", "Value")}
                            placeholder={t(
                                "backoffice.discount_form.enter_value",
                                "Enter value"
                            )}
                            value={item.value}
                            onChange={value =>
                                onDiscountTemplateChange(
                                    value === null ? 0 : value,
                                    "value",
                                    index
                                )
                            }
                            unit="%"
                            min={0}
                            max={1}
                            percentageToRate={true}
                            decimals={2}
                            required={true}
                        />
                    </InputControl>
                ) : (
                    <InputControl key="amount">
                        <NumberInput
                            label={t(
                                "backoffice.discount_form.amount",
                                "Amount"
                            )}
                            placeholder={t(
                                "backoffice.discount_form.enter_amount",
                                "Enter amount"
                            )}
                            value={item.value / 100}
                            onChange={value =>
                                onDiscountTemplateChange(
                                    value === null ? 1 : value * 100,
                                    "value",
                                    index
                                )
                            }
                            unit={currency}
                            min={0}
                            decimals={2}
                            required={true}
                        />
                    </InputControl>
                )}

                <ColorPickerInput
                    defaultColor={item.color || undefined}
                    label={t(
                        "backoffice.product_form.enter_discount_template_color",
                        "Button color"
                    )}
                    onChangeColor={value => {
                        onDiscountTemplateChange(value, "color", index);
                    }}
                    style={styles.colorPickerElevation}
                />
            </View>
        </RemovableRow>
    );
});

export const DiscountTemplateSection = React.memo(
    function DiscountTemplateSection({
        values,
        errors,
        styles,
        theme,
        t,
        currency,
        onAddDiscountTemplate,
        onDiscountTemplateChange,
        onRemoveDiscountTemplate,
    }: {
        values: IProductInput;
        errors: Form<IProductInput>[0]["errors"];
        styles: StyleData;
        theme: ReturnType<typeof useTheme>;
        t: ReturnType<typeof useTranslation>[0];
        currency: SupportedCurrenciesType;
        onAddDiscountTemplate: () => void;
        onDiscountTemplateChange: (
            value: any,
            type: "name" | "type" | "value" | "color",
            index: number
        ) => void;
        onRemoveDiscountTemplate: (_item: any, index: number) => void;
    }) {
        const discountTemplateItems = useMemo(() => {
            return values.discountTemplate?.map((item, index) => (
                <DiscountTemplateRow
                    key={`discount_template_row:${item.name}_${index}`}
                    item={item}
                    index={index}
                    isExternal={values.isExternal}
                    currency={currency}
                    styles={styles}
                    theme={theme}
                    t={t}
                    onDiscountTemplateChange={onDiscountTemplateChange}
                    onRemoveDiscountTemplate={onRemoveDiscountTemplate}
                />
            ));
        }, [
            currency,
            onDiscountTemplateChange,
            onRemoveDiscountTemplate,
            styles,
            t,
            theme,
            values.discountTemplate,
            values.isExternal,
        ]);

        return (
            <>
                <Accordion
                    title={`${t(
                        "backoffice.product_form.discount_templates",
                        "Discount templates"
                    )}${
                        values.discountTemplate?.length > 0
                            ? " (" + values.discountTemplate?.length + ")"
                            : ""
                    }`}
                    showContents={false}
                    textStyle={theme.styles.medium}
                >
                    <InputControl
                        error={errors.discountTemplate}
                        description={
                            values.isExternal
                                ? t(
                                      "backoffice.common.readonly_value",
                                      "This value is controlled by an external system via the API and can only be changed from here."
                                  )
                                : t(
                                      "backoffice.product_form.discount_template_description",
                                      "Enter one or more discounts for the product by pressing +"
                                  )
                        }
                    >
                        <InputLabel>
                            {t(
                                "backoffice.product_form.discount_template",
                                "Discount template"
                            )}
                            {!values.isExternal && (
                                <IconButton
                                    name="plus"
                                    size="small"
                                    onPress={onAddDiscountTemplate}
                                    style={styles.addDiscountTemplate}
                                />
                            )}
                        </InputLabel>

                        <Spacer />

                        {discountTemplateItems}
                    </InputControl>
                </Accordion>
                <Divider />
                <Spacer space={2} />
            </>
        );
    }
);
